function formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision, divisor) {
  const roundedNumber = (moneyValue / divisor).toFixed(precision);
  let [a, b] = roundedNumber.split('.');
  if (!a) a = '0';
  if (!b) b = '';
  a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);
  return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
}

export function stripPlainMoneySeparators(value) {
  let result = String(value);
  result = result.replace(/(?<=\d)[\s\u00a0\u202f'’](?=\d{3}(?:\D|$))/g, '');
  result = result.replace(/(?<=\d),(?=\d{3}(?:\D|$))/g, '');
  result = result.replace(/(?<=\d)\.(?=\d{3}(?:\D|$))/g, '');
  return result;
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function normalizePlainMoneySpacing(value, moneyFormat = '{{amount}}') {
  let result = String(value);
  const prefix = moneyFormat.split('{{')[0].trim();

  if (prefix) {
    const escapedPrefix = escapeRegExp(prefix);
    result = result.replace(new RegExp(`${escapedPrefix}(?=\\S)`), `${prefix} `);
  }

  const suffix = moneyFormat.split('}}').pop()?.trim() ?? '';
  if (suffix) {
    const escapedSuffix = escapeRegExp(suffix);
    result = result.replace(new RegExp(`(\\S)${escapedSuffix}`), `$1 ${suffix}`);
  }

  result = result.replace(/(\d)[.,](\d{1,2})(?![.\d])/g, '$1 $2');

  return result.replace(/\s{2,}/g, ' ');
}

export function resolveMoneyFormat(display, formats = {}) {
  const {
    moneyFormat = '{{amount}}',
    moneyWithCurrencyFormat = '{{amount}}',
    moneyFormatNoDecimals = '{{amount_no_decimals}}',
  } = formats;

  switch (display) {
    case 'money_with_currency':
      return moneyWithCurrencyFormat;
    case 'money_without_currency':
      return '{{amount}}';
    case 'money_without_trailing_zeros':
      return moneyFormatNoDecimals;
    default:
      return moneyFormat;
  }
}

export function formatThemeMoney(moneyValue, options = {}) {
  const {
    display = 'money',
    separator = 'default',
    moneyFormat = '{{amount}}',
    moneyWithCurrencyFormat = '{{amount}}',
    moneyFormatNoDecimals = '{{amount_no_decimals}}',
    currency = 'UAH',
  } = options;

  const format = resolveMoneyFormat(display, {
    moneyFormat,
    moneyWithCurrencyFormat,
    moneyFormatNoDecimals,
  });

  let formatted = formatMoney(moneyValue, format, currency);

  if (separator === 'plain') {
    formatted = normalizePlainMoneySpacing(stripPlainMoneySeparators(formatted), moneyFormat);
  }

  return formatted;
}

export function formatMoney(moneyValue, format, currency) {
  const currencyPrecision = 2;
  const divisor = Math.pow(10, currencyPrecision);

  return format.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
    if (typeof placeholder !== 'string') return '';
    if (placeholder === 'currency') return currency;

    let thousandsSeparator = ',';
    let decimalSeparator = '.';
    let precision = currencyPrecision;

    switch (placeholder) {
      case 'amount':
        break;
      case 'amount_no_decimals':
        precision = 0;
        break;
      case 'amount_with_comma_separator':
        thousandsSeparator = '.';
        decimalSeparator = ',';
        break;
      case 'amount_no_decimals_with_comma_separator':
        thousandsSeparator = '.';
        precision = 0;
        break;
      case 'amount_no_decimals_with_space_separator':
        thousandsSeparator = ' ';
        precision = 0;
        break;
      case 'amount_with_space_separator':
        thousandsSeparator = ' ';
        decimalSeparator = ',';
        break;
      case 'amount_with_period_and_space_separator':
        thousandsSeparator = ' ';
        decimalSeparator = '.';
        break;
      case 'amount_with_apostrophe_separator':
        thousandsSeparator = "'";
        decimalSeparator = '.';
        break;
      default:
        break;
    }

    return formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision, divisor);
  });
}

export function debounce(fn, wait) {
  let timeout;

  function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  }

  debounced.cancel = () => {
    clearTimeout(timeout);
  };

  return debounced;
}

export const setDocumentScrollbarPadding = () => {
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

  if (scrollbarWidth <= 0) return undefined;

  const previousPadding = document.documentElement.style.paddingRight;
  document.documentElement.style.paddingRight = `${scrollbarWidth}px`;
  document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);

  return previousPadding;
};

export const resetDocumentScrollbarPadding = (previousPadding) => {
  if (previousPadding === undefined) return;

  document.documentElement.style.paddingRight = previousPadding;
  document.documentElement.style.removeProperty('--scrollbar-width');
};

let scrollLocksCount = 0;
let previousPadding = undefined;

export const lockPageScroll = () => {
  if (scrollLocksCount === 0) {
    previousPadding = setDocumentScrollbarPadding();
    document.documentElement.classList.add('is-scroll-locked');
  }
  scrollLocksCount++;
};

export const unlockPageScroll = (force = false) => {
  if (force) {
    scrollLocksCount = 0;
  } else if (scrollLocksCount > 0) {
    scrollLocksCount--;
  }

  if (scrollLocksCount === 0) {
    document.documentElement.classList.remove('is-scroll-locked');
    resetDocumentScrollbarPadding(previousPadding);
    previousPadding = undefined;
  }
};

export function planForMonths(plans, months) {
  for (const plan of plans) {
    const name = (plan.name || '').toLowerCase().replaceAll('-', ' ');

    if (months === 6) {
      if (name.includes('180') || name.includes('6 month')) return plan;
    }

    if (months === 3) {
      if (name.includes('90') || name.includes('3 month')) return plan;
    }

    if (months === 1) {
      if (
        !name.includes('90')
        && !name.includes('180')
        && !name.includes('3 month')
        && !name.includes('6 month')
      ) {
        return plan;
      }
    }
  }

  return null;
}
